// A value an operator has to carry OUT of the screen, and the button that
// carries it.
//
// The deployment request's first-login address is the first of these: the
// platform deploy takes it as an input, so it is hand-carried from this screen
// into another tool, and a mistyped one produces an instance nobody can log
// into — discovered days later, by the person who cannot get in. Selecting an
// address out of a table cell by dragging is exactly where that typo comes
// from, and it is why this is a button rather than trust in the mouse.
//
// The text stays REAL TEXT beside the button rather than being hidden behind
// it: an operator reads the address to sanity-check it before they use it, and
// a screen that will only surrender a value through the clipboard cannot be
// read at a glance or over somebody's shoulder.
//
// `navigator.clipboard` is absent on an insecure origin and can be refused by
// permission policy, and this says which happened rather than flashing
// "Copied" over a clipboard that holds the previous thing. The operator can
// still select the text — the failure costs them the shortcut, not the value.
import { useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { Button } from '@miq/design-system/components/ui/button';

/** How long the tick stands in for the copy icon. Long enough to be seen. */
const COPIED_FOR_MS = 2000;

export interface CopyableValueProps {
  /** The exact characters that go to the clipboard, and that are shown. */
  value: string;
  /** Names the button for a screen reader, e.g. "first login address". */
  label: string;
  /** Drawn instead of the value where there is none. */
  absent?: string;
}

export function CopyableValue({ value, label, absent = '—' }: CopyableValueProps) {
  const [copied, setCopied] = useState(false);
  const [refused, setRefused] = useState('');

  if (!value) {
    return <span className="text-muted-foreground">{absent}</span>;
  }

  // Not an async handler: `onPress` wants a void return, and an async one
  // hands it a promise nothing is waiting on. The chain says the same thing
  // and keeps the rejection where it can be shown.
  const copy = () => {
    setRefused('');
    if (!navigator.clipboard) {
      setRefused('This browser will not give the page a clipboard here.');
      return;
    }
    navigator.clipboard.writeText(value).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), COPIED_FOR_MS);
      },
      () => {
        setRefused(
 'The browser refused the clipboard. Select the text instead.',
        );
      },
    );
  };

  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-1">
        <span className="font-mono text-xs">{value}</span>
        <Button
          variant="ghost"
          size="icon"
          className="size-6 shrink-0"
          aria-label={copied ? `Copied the ${label}` : `Copy the ${label}`}
          onPress={copy}
        >
          {copied ? (
            <Check className="size-3.5 text-success" />
          ) : (
            <Copy className="size-3.5" />
          )}
        </Button>
      </span>
      {refused ? (
        <span role="alert" className="text-xs text-destructive">
          {refused}
        </span>
      ) : null}
    </span>
  );
}
